import React, { useState, useEffect } from "react";
import {
  TabbedForm,
  Tab,
  Edit,
  TextField,
  TabbedShowLayout,
  BooleanInput,
  SimpleShowLayout,
  useNotify,
  useRecordContext,
  SelectInput,
  DateInput,
  useResourceContext,
  ListButton,
  RefreshButton,
  TopToolbar,
} from "react-admin";
import { Grid, Typography, Box } from "@mui/material";
import { RUN_TIME_API_ENDPOINT, CommonHeaderOption } from "../common/common";
import { useParams } from "react-router-dom";
import Swal from 'sweetalert2';

const PostTitle = () => {
    const record = useRecordContext();
    return <span>{record ? `${record.name}` : ''}</span>;
};

const OrderingDayOff = () => {
    const record = useRecordContext();
    if (!record || !record.ordering_day_off || record.ordering_day_off.length === 0) return "-";
    return (
        <ul style={{ paddingLeft: "20px", margin: "0" }}>
            {record.ordering_day_off.map((date, index) => (
                <li key={index} style={{ marginBottom: "5px" }}>
                    {formatDate(date, "short")}
                </li>
            ))}
        </ul>
    );
};

const formatDate = (dateString, form) => {
    if (!dateString) return "";
    
    const date = new Date(dateString);
    const day = date.toLocaleDateString("en-GB", { weekday: form });
    const dayNumber = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: form });
    let year = date.getFullYear();
    year = form == "short" ? year.toString().slice(-2) : year

    // Adding ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (num) => {
        if (num > 3 && num < 21) return "th";
        switch (num % 10) {
            case 1: return "st";
            case 2: return "nd";
            case 3: return "rd";
            default: return "th";
        }
    };

    return `${day}, ${dayNumber}${getOrdinalSuffix(dayNumber)} ${month} ${year}`;
};

const TimeSlotsTable = ({ timeSlots, setTimeSlots }) => {
    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    const times = ["0600", "0630", "0700", "0730", "0800", "0830", "0900", "0930", "1000", "1030", "1100", "1130", "1200", "1230", "1300", "1330", "1400", "1430"];
    const toggleSlot = (day, time) => {

        setTimeSlots((prev) => ({
            ...prev,
            [day]: { ...prev[day], [time]: !prev[day]?.[time] }
        }));
    };

    return (
        <div style={styles.tableContainer}>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.cellth}>Time</th>
                        {days.map((day) => (
                            <th key={day} style={styles.cellth}>{day.charAt(0).toUpperCase() + day.slice(1)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="striped-tbody">
                    {times.map((time) => (
                        <tr key={time}>
                            <td style={styles.cell}>{time}</td>
                            {days.map((day) => (
                                <td key={day} style={styles.cell}>
                                    <input
                                        type="checkbox"
                                        checked={timeSlots?.[day]?.[time] || false}
                                        onChange={() => toggleSlot(day, time)}
                                        style={styles.checkbox}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const PlantsEdit = (props) => {
  const { id } = useParams();
  const apiUrl = RUN_TIME_API_ENDPOINT;
  const [timeSlots, setTimeSlots] = useState({});
  const [leadTime, setLeadTime] = useState("");
  const [sameDayOrdering, setSameDayOrdering] = useState(false);
  const [useTimeSlots, setUseTimeSlots] = useState(false);
  // const [holdOrdersReview, setHoldOrdersReview] = useState(false);
  const [closedDates, setClosedDates] = useState([]);
  const [showClosedDates, setShowClosedDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [minimumMix, setMinimumMix] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearsList, setYearsList] = useState([]);
  const notify = useNotify();
  // Fetch existing plant data
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/sap_plants/${id}`,
          CommonHeaderOption()
        );
        if (!response.ok) throw new Error("Failed to fetch data");
        const data = await response.json();
        setTimeSlots(data.data.timeslots);
        setLeadTime(data.data.lead_time || 0);
        setMinimumMix(data.data.minimum_mix || 0);
        setSameDayOrdering(data.data.same_day_ordering || false);
        setUseTimeSlots(data.data.use_time_slots || false);
        // setHoldOrdersReview(data.data.hold_orders_review || false);
        setClosedDates(data.data.ordering_day_off || []);

        if(data?.data?.ordering_day_off && Array.isArray(data?.data?.ordering_day_off)) {
          const uniqueYears = [...new Set(data.data.ordering_day_off.map(date => new Date(date).getFullYear()))].sort((a, b) => a - b);
          setSelectedYear(uniqueYears[0]);
          setYearsList(uniqueYears)

          const filteredDates = data.data.ordering_day_off.filter((date) => new Date(date).getFullYear() === uniqueYears[0]);
          setShowClosedDates(filteredDates);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const uniqueYears = [...new Set(closedDates.map(date => new Date(date).getFullYear()))].sort((a, b) => a - b);
    const year = selectedYear || new Date().getFullYear();

    let filteredDates;
    filteredDates = closedDates.filter((date) => new Date(date).getFullYear() == year);

    if(filteredDates.length == 0) {
      filteredDates = closedDates.filter((date) => new Date(date).getFullYear() == uniqueYears[0]);
    }

    setYearsList(uniqueYears)
    setShowClosedDates(filteredDates);

    if(!selectedYear) {
      setSelectedYear(uniqueYears[0])
    }
  }, [selectedYear, closedDates])

  const saveSlots = async () => {
    if (!id) return alert("Plant ID is missing!");
    setLoading(true);
    const payload = {
      timeslots: timeSlots,
      lead_time: leadTime,
      same_day_ordering: sameDayOrdering,
      use_time_slots: useTimeSlots,
      // hold_orders_review: holdOrdersReview,
      minimum_mix: minimumMix,
    };

    try {
      const response = await fetch(`${apiUrl}/sap_plants/${id}`, {
        method: "PUT",
        ...CommonHeaderOption(),
        body: JSON.stringify(payload),
      });

      if (!response.ok)
        throw new Error(`Failed to save data: ${response.statusText}`);
      notify("Data saved successfully", { type: "success" });
      setLoading(false);
      // alert("Data saved successfully!");
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save data. Please try again.");
    }
  };

  const saveClosedDate = async () => {
    if (!selectedDate) return alert("Please select a date");
    const formattedSelectedDate = new Date(selectedDate)
      .toISOString()
      .split("T")[0];
    if (
      closedDates.some(
        (date) =>
          new Date(date).toISOString().split("T")[0] === formattedSelectedDate
      )
    ) {
      notify("This date is already added.", { type: "error" });
      return;
    }
    if (new Date().toISOString().split("T")[0] > formattedSelectedDate) {
      notify("Past date can not be added.", { type: "error" });
      return;
    }
    const url = `${apiUrl}/sap_plants/${id}`;
    const payload = { ordering_day_off: [...closedDates, selectedDate] };
    try {
      const response = await fetch(url, {
        method: "PUT",
        ...CommonHeaderOption(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      const result = await response.json();
      setClosedDates([...closedDates, selectedDate]);
      setSelectedDate(null);
      setSelectedYear(new Date(selectedDate).getFullYear())
      notify("Closed date saved successfully", { type: "success" });
      // redirect(false);
    } catch (error) {
      console.error("Error saving closed date:", error);
      alert("Failed to save closed date.");
    }
  };

  const deleteClosedDate = async (dateToDelete) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to delete ${formatDate(dateToDelete, 'long')}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Close",
    });

    if (!result.isConfirmed) return;

    const url = `${apiUrl}/sap_plants/${id}`;
    const payload = {
      ordering_day_off: closedDates.filter((date) => date !== dateToDelete),
    };
    try {
      const response = await fetch(url, {
        method: "PUT",
        ...CommonHeaderOption(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      setClosedDates(closedDates.filter((date) => date !== dateToDelete));
    } catch (error) {
      console.error("Error deleting closed date:", error);
      alert("Failed to delete closed date.");
    }
  };

  const ShowActions = () => {
    const resource = useResourceContext();
    return (
      <React.Fragment >
        <TopToolbar>
          <RefreshButton />
          <ListButton basePath={resource} />
        </TopToolbar>
      </React.Fragment >
    )
  }

  return (
    <Edit 
      actions={<ShowActions/>}
      title={<PostTitle />} 
      {...props} 
      mutationMode="pessimistic"
    >
      <TabbedForm toolbar={null}>
        <TabbedForm.Tab label="Summary">
          <SimpleShowLayout>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Name
                </Typography>
                <TextField source="name" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Plant Code
                </Typography>
                <TextField source="plant_code" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Lead Time
                </Typography>
                <TextField source="lead_time" emptyText="N/A" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Minimum Mix
                </Typography>
                <TextField source="minimum_mix" emptyText="N/A" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Same Day Ordering Not Allowed
                </Typography>
                <TextField source="same_day_ordering" />
              </Grid>
              {/* <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Hold Orders for Review
                </Typography>
                <BooleanField source="hold_orders_review" />
              </Grid> */}
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Rocks Active
                </Typography>
                <TextField source="is_rocks_enable" />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Ordering Day Off
                </Typography>
                <OrderingDayOff source="ordering_day_off" />
              </Grid>
            </Grid>
          </SimpleShowLayout>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Time Slots">
          {/* <TextInput label="Lead time (minutes)" source="lead_time" onChange={(e) => setLeadTime(e.target.value)} defaultValue={leadTime} /> */}
          {/* <BooleanInput label="Do not use timeslots" source="use_time_slots" onChange={(e) => setUseTimeSlots(e.target.checked)} defaultValue={useTimeSlots} /> */}
          <Grid container spacing={10} justifyContent="center">
            <Grid item xs={3}>
              <SelectInput
                fullWidth
                source="lead_time"
                label="Lead time (minutes)"
                choices={[
                  { id: 0, name: "None", value: 0 },
                  { id: 30, name: 30, value: 30 },
                  { id: 60, name: 60, value: 60 },
                  { id: 90, name: 90, value: 90 },
                  { id: 120, name: 120, value: 120 },
                ]}
                onChange={(e) => setLeadTime(e.target.value)}
              />
            </Grid>
            {/* <Grid item xs={5}>
              <BooleanInput
                fullWidth
                label="Hold orders for review"
                source="hold_orders_review"
                onChange={(e) => setHoldOrdersReview(e.target.checked)}
                defaultValue={holdOrdersReview}
              />
            </Grid> */}
            <Grid item xs={3}>
              <SelectInput
                fullWidth
                source="minimum_mix"
                label="Minimum Mix (Ton)"
                choices={[
                  { id: 0, name: "None", value: 0 },
                  { id: 0.5, name: 0.5, value: 0.5 },
                  { id: 1, name: 1, value: 1 },
                  { id: 1.5, name: 1.5, value: 1.5 },
                  { id: 2, name: 2, value: 2 },
                  { id: 2.5, name: 2.5, value: 2.5 },
                  { id: 3, name: 3, value: 3 },
                ]}
                onChange={(e) => setMinimumMix(e.target.value)}
              />
            </Grid>

            <Grid item xs={3} mt={2}>
              <BooleanInput
                fullWidth
                label="Allow same day ordering"
                source="same_day_ordering"
                onChange={(e) => setSameDayOrdering(e.target.checked)}
                defaultValue={sameDayOrdering}
              />
            </Grid>
          </Grid>
          <TimeSlotsTable timeSlots={timeSlots} setTimeSlots={setTimeSlots} />

          <button
            type="button"
            style={styles.button}
            onClick={saveSlots}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </TabbedForm.Tab>
        <TabbedForm.Tab label="Closed Date">
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={2}>
              <DateInput
                label="Select Closed Date"
                source="closed_date"
                onChange={(date) => setSelectedDate(date?.target?.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={2} justifyContent="center" alignItems="center">
              <button
                type="button"
                style={
                  selectedDate
                    ? styles.button
                    : { ...styles.button, backgroundColor: "grey" }
                }
                onClick={saveClosedDate}
                disabled={!selectedDate}
              >
                Save
              </button>
            </Grid>
          </Grid>

          {closedDates && closedDates.length > 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                width: '100%', 
                marginBottom: 3
              }}
            >
              <Box sx={{ width: 200 }}>
                <label htmlFor="year-select" style={{ fontSize: "15px", marginLeft: 65 }}>
                  Select Year
               </label>
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "10px",
                    marginTop: "5px",
                    fontSize: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    appearance: "none",
                    backgroundColor: "white",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 10px center",
                    backgroundSize: "12px 8px",
                  }}
                >
                  <option value="" disabled>
                    -- Select Year --
                  </option>
                  {yearsList.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </Box>
            </Box>
          )}

          {showClosedDates?.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.cell}>Action</th>
                  <th style={styles.cell}>Closed Date</th>
                </tr>
              </thead>

              <tbody>
                {[...showClosedDates]
                  .sort((a, b) => new Date(b) - new Date(a)) // Sort in ascending order
                  .map((date, index) => {
                    const isPastDate =
                      new Date(date) <
                      new Date(new Date().setHours(0, 0, 0, 0)); // Check if date is past
                    return (
                      <tr
                        key={index}
                        // style={{ color: isPastDate ? "grey" : "black" }}
                        style={{ color: "black" }}
                      >
                        {" "}
                        {/* Apply red color for past dates */}
                        <td style={styles.cell}>
                          <button
                            type="button"
                            // style={
                            //   !isPastDate
                            //     ? styles.deleteButton
                            //     : {
                            //         ...styles.deleteButton,
                            //         backgroundColor: "grey",
                            //       }
                            // }
                            style={styles.deleteButton}
                            // disabled={isPastDate}
                            onClick={() => deleteClosedDate(date)}
                          >
                            Delete
                          </button>
                        </td>
                        <td
                          style={{
                            ...styles.cell,
                            // fontWeight: isPastDate ? "bold" : "normal",
                            fontWeight: "normal",
                          }}
                        >
                          {formatDate(date, "long")}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </TabbedForm.Tab>
      </TabbedForm>
    </Edit>
  );
};

const styles = {
    tableContainer: {
        overflowX: "auto",
        maxHeight: "500px",
        border: "1px solid #ddd",
        padding: "10px",
        borderRadius: "8px",
        background: "#f9f9f9",
        maxWidth: "100%",
        minWidth: "100%"
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        whiteSpace: "nowrap",
    },
    cellth:
    {
        padding: "8px",
        textAlign: "center",
        border: "1px solid #ddd",
        background: "#fff",
        fontWeight: "bold",  
    },
    cell: {
        padding: "8px",
        textAlign: "center",
        border: "1px solid #ddd",
        fontWeight: "bold",
    },
    checkbox: {
        width: "18px",
        height: "18px",
        cursor: "pointer",
        accentColor: "#007bff",
    },
    button: {
        marginTop: "10px",
        marginBottom: "10px",
        padding: "10px 20px",
        backgroundColor: "#007bff",
        color: "#fff",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        fontSize: "16px",
        marginLeft:"auto"
    },
    deleteButton: {
        marginLeft: "10px",
        padding: "5px 10px",
        backgroundColor: "red",
        color: "white",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        fontSize: "12px",
    }
};

export default PlantsEdit;